import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface SyncOrganizationProfileParams {
  organizationId: string;
  profileData: any;
}

export const useSyncOrganizationProfile = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ organizationId, profileData }: SyncOrganizationProfileParams) => {
      console.log('🔄 Starting organization profile sync...', { organizationId, profileData });
      
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('No active session');
      }

      console.log('✅ Session verified, invoking sync function...');

      const response = await supabase.functions.invoke('sync-organization-profile', {
        body: {
          organizationId,
          profileData,
        },
      });

      console.log('📡 Sync function response:', response);

      if (response.error) {
        console.error('❌ Sync function error:', response.error);
        throw new Error(response.error.message || 'Failed to sync profile');
      }

      if (response.data?.success) {
        console.log('✅ Profile sync successful');
        
        // Invalidate related queries to ensure UI consistency
        queryClient.invalidateQueries({ queryKey: ['organizations'] });
        queryClient.invalidateQueries({ queryKey: ['companies'] });
        queryClient.invalidateQueries({ queryKey: ['organization-details'] });
        
        return response.data;
      } else {
        console.error('❌ Sync failed:', response.data);
        throw new Error(response.data?.error || 'Unknown sync error');
      }
    },
    onError: (error) => {
      console.error('💥 Sync mutation failed:', error);
      toast.error(`Sync failed: ${error.message}`);
    },
    onSuccess: (data) => {
      console.log('🎉 Sync mutation succeeded:', data);
      toast.success('Profile synced successfully to external systems');
    },
    retry: 2,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });
};